/**
 * Tempo / speed math for the FamiTracker timing model.
 *
 * FamiTracker uses two parameters to control playback speed:
 *
 *   - **speed**: ticks per row (lower = faster)
 *   - **tempo**: a BPM-like value (higher = faster), with 150 as the
 *     "neutral" reference where one tick = one NES frame.
 *
 * The actual timing comes from the NES vertical sync (60.0988 Hz NTSC).
 * Each row consumes `speed` ticks, modulated by tempo. At the reference
 * tempo of 150, frames_per_row = speed exactly. At higher tempos rows
 * fire faster; at lower tempos slower.
 *
 *   frames_per_row     = speed * (150 / tempo)
 *   row_duration_sec   = frames_per_row / 60.0988
 *   rows_per_second    = tempo * 60.0988 / (150 * speed)
 *
 * Default project values are speed=6, tempo=150 → 10 rows/sec exactly
 * which is 4 rows/beat at BPM 150. The standard chiptune tempo.
 */

import { NES } from '@pulsewright/shared';

/** Reference tempo at which 1 tick === 1 NES frame. */
export const TEMPO_REFERENCE = 150;

/** Compute how many NES frames make up one tracker row. */
export function framesPerRow(speed: number, tempo: number): number {
  if (speed <= 0) throw new Error(`speed must be > 0 (got ${speed})`);
  if (tempo <= 0) throw new Error(`tempo must be > 0 (got ${tempo})`);
  return (speed * TEMPO_REFERENCE) / tempo;
}

/** Compute the duration of one tracker row in seconds (NTSC). */
export function rowDurationSec(speed: number, tempo: number): number {
  return framesPerRow(speed, tempo) / NES.FRAME_RATE_HZ;
}

/** Compute how many rows play per second at the given speed/tempo. */
export function rowsPerSecond(speed: number, tempo: number): number {
  return 1 / rowDurationSec(speed, tempo);
}

/**
 * Convenience: how long is one beat (typically 4 rows) in seconds?
 * Returns NaN for non-positive rowsPerBeat.
 */
export function beatDurationSec(speed: number, tempo: number, rowsPerBeat = 4): number {
  if (rowsPerBeat <= 0) return NaN;
  return rowDurationSec(speed, tempo) * rowsPerBeat;
}

/** Convenience: effective BPM given rowsPerBeat (default 4). */
export function effectiveBPM(speed: number, tempo: number, rowsPerBeat = 4): number {
  return 60 / beatDurationSec(speed, tempo, rowsPerBeat);
}
